"use client";

import { AuthUserEntity, V2TokenEntity } from "@yardzen/api-dtos";
import { AxiosResponse } from "axios";
import { apiClient } from "../base-client";

const baseUrl = "auth";

export interface LinkCredentialsResponse {
  success: boolean;
  uid: string;
  loginUrl?: string;
}

export async function getCurrentUser(): Promise<AxiosResponse<AuthUserEntity>> {
  const response = await apiClient.get(`${baseUrl}/current-user`);
  return {
    ...response,
    data: AuthUserEntity.from(response.data),
  };
}

export async function getV2Token(): Promise<AxiosResponse<V2TokenEntity>> {
  // This creates a new axios client that will also send the cookies.
  // We need to generate a v2 token from a v1 token, but the v1 token is
  // HTTP only meaning only the server can read it.
  const response = await apiClient.post(`${baseUrl}/v2-token`);
  return {
    ...response,
    data: V2TokenEntity.from(response.data),
  };
}

/**
 * Links email/password credentials to the current user's Firebase account.
 * This is used during the welcome modal flow to set up password auth after checkout.
 */
export async function linkCredentials(
  email: string,
  password: string,
): Promise<AxiosResponse<LinkCredentialsResponse>> {
  return apiClient.post("auth-util/link-credentials", { email, password });
}

export interface AuthProviderStatusResponse {
  hasPassword: boolean;
  hasNonAnonymousProvider: boolean;
}

/**
 * Returns the current user's Firebase auth provider status. Used by the
 * welcome modal to decide whether to prompt for a password: anonymous users
 * need one (and can link via Admin SDK), federated users (e.g. Google) do not.
 */
export async function getAuthProviderStatus(): Promise<
  AxiosResponse<AuthProviderStatusResponse>
> {
  return apiClient.get("auth-util/auth-provider-status");
}
